"use client";

import { useState, useEffect, useRef } from "react";
import { Link } from "@/i18n/routing";
import Image from "next/image";
import { usePathname } from "next/navigation";
import { X, ChevronDown, Calendar, Wrench, Gift, ShoppingBag } from "lucide-react";
import { useSession } from "next-auth/react";
import { useTranslations } from "next-intl";
import { cn } from "@/lib/utils";
import { NAV_LINKS } from "@/lib/constants";
import { readClientScope, getActiveScope, type QuickLinkScope } from "@/lib/quick-link-scope";
import LanguageSwitcher from "./LanguageSwitcher";
import CurrencySwitcher from "./CurrencySwitcher";

const SCOPE_ITEMS: Record<string, { label: string; href: string; Icon: typeof Calendar }> = {
  book: { label: "Book a Service", href: "/book", Icon: Calendar },
  accessories: { label: "Accessories", href: "/accessories", Icon: ShoppingBag },
  service_pickup: { label: "Service & Repair", href: "/account/service-pickup", Icon: Wrench },
  gift_cards: { label: "Gift Cards", href: "/gift-cards", Icon: Gift },
};

interface MobileNavProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function MobileNav({ isOpen, onClose }: MobileNavProps) {
  const [expandedItems, setExpandedItems] = useState<string[]>([]);
  const [showLinks, setShowLinks] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const { data: session } = useSession();
  const t = useTranslations("nav");
  const tc = useTranslations("common");
  const pathname = usePathname();
  const [scope, setScope] = useState<QuickLinkScope | null>(null);

  useEffect(() => {
    // Path-aware: only apply scope inside the scoped flow. See
    // getActiveScope() docblock for details.
    setScope(getActiveScope(readClientScope(), pathname));
  }, [pathname, isOpen]);

  // exitScope handler removed — see Header.tsx comment.

  const toggleExpand = (label: string) => {
    setExpandedItems((prev) =>
      prev.includes(label)
        ? prev.filter((item) => item !== label)
        : [...prev, label]
    );
  };

  // Stagger link entrances after the overlay opens
  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => setShowLinks(true), 150);
      return () => clearTimeout(timer);
    } else {
      setShowLinks(false);
    }
  }, [isOpen]);

  // Focus trap, Escape key, and body scroll lock
  useEffect(() => {
    if (!isOpen) return;

    // Focus the close button when panel opens
    setTimeout(() => closeButtonRef.current?.focus(), 200);

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
        return;
      }

      if (e.key !== "Tab" || !panelRef.current) return;

      const focusableSelectors =
        'button:not([disabled]), [href], input:not([disabled]), [tabindex]:not([tabindex="-1"])';
      const focusableElements = Array.from(
        panelRef.current.querySelectorAll<HTMLElement>(focusableSelectors)
      );

      if (focusableElements.length === 0) return;

      const first = focusableElements[0];
      const last = focusableElements[focusableElements.length - 1];

      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onClose]);

  return (
    <>
      {/* Full-screen backdrop with blur */}
      <div
        className={cn(
          "fixed inset-0 z-50 bg-brand-dark/70 backdrop-blur-md transition-opacity duration-500 ease-out",
          isOpen ? "opacity-100" : "opacity-0 pointer-events-none"
        )}
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Full-screen overlay panel */}
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={tc("mobileNavigation")}
        className={cn(
          "fixed inset-0 z-50 bg-brand-dark text-white",
          "transition-all duration-500 ease-out",
          "flex flex-col",
          isOpen
            ? "opacity-100 visible"
            : "opacity-0 invisible pointer-events-none"
        )}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-white/10">
          {/* Use the transparent-bg logo (RGBA) — logo-dark.png is RGB
              with a baked-in solid black box that didn't match the
              drawer's bg-brand-dark, leaving a visible edge. The gold
              "RW" mark on transparent renders cleanly on the dark drawer. */}
          <Image src="/images/logo.png?v=2" alt="Rivky Wigs" width={100} height={50} className="h-8 w-auto" />
          <button
            ref={closeButtonRef}
            onClick={onClose}
            className={cn(
              "p-3 -me-3 min-h-[44px] min-w-[44px] flex items-center justify-center",
              "text-white/70 hover:text-white hover:rotate-90",
              "transition-all duration-500 ease-out",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold/50"
            )}
            aria-label={tc("closeMenu")}
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Nav Links with staggered entrances */}
        <nav className="flex-1 overflow-y-auto py-8 px-2" aria-label={tc("mobileNavigation")}>
          {scope ? (
            <ul className="space-y-1">
              {scope.features.map((key, index) => {
                const item = SCOPE_ITEMS[key];
                if (!item) return null;
                const { Icon } = item;
                return (
                  <li
                    key={key}
                    className={cn(
                      "transition-all duration-500 ease-out",
                      showLinks ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
                    )}
                    style={{ transitionDelay: showLinks ? `${index * 60}ms` : "0ms" }}
                  >
                    <Link
                      href={item.href}
                      onClick={onClose}
                      className={cn(
                        "flex items-center gap-4 px-6 py-4 min-h-[52px]",
                        "text-base font-medium tracking-[0.15em] uppercase",
                        "text-white/90 hover:text-white hover:bg-white/5",
                        "transition-all duration-500 rounded-lg"
                      )}
                    >
                      <Icon className="h-5 w-5 text-brand-gold" />
                      {item.label}
                    </Link>
                  </li>
                );
              })}
            </ul>
          ) : (
          <ul className="space-y-1">
            {NAV_LINKS.map((link, index) => {
              const hasChildren = link.children && link.children.length > 0;
              const isExpanded = expandedItems.includes(link.label);

              return (
                <li
                  key={link.label}
                  className={cn(
                    "transition-all duration-500 ease-out",
                    showLinks
                      ? "opacity-100 translate-y-0"
                      : "opacity-0 translate-y-4"
                  )}
                  style={{
                    transitionDelay: showLinks ? `${index * 60}ms` : "0ms",
                  }}
                >
                  {hasChildren ? (
                    <>
                      <button
                        onClick={() => toggleExpand(link.label)}
                        aria-expanded={isExpanded}
                        aria-controls={`mobile-nav-dropdown-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                        className={cn(
                          "flex items-center justify-between w-full px-6 py-4 min-h-[52px]",
                          "text-base font-medium tracking-[0.15em] uppercase",
                          "text-white/90 hover:text-white hover:bg-white/5",
                          "transition-all duration-500 rounded-lg"
                        )}
                      >
                        <span>{t(link.tKey)}</span>
                        <ChevronDown
                          className={cn(
                            "h-4 w-4 transition-transform duration-500 ease-out",
                            isExpanded && "rotate-180"
                          )}
                        />
                      </button>
                      <div
                        id={`mobile-nav-dropdown-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                        className={cn(
                          "overflow-hidden transition-all duration-500 ease-out",
                          isExpanded ? "max-h-96 opacity-100" : "max-h-0 opacity-0"
                        )}
                      >
                        <ul className="py-2 ps-4">
                          {link.children!.map((child) => (
                            <li key={child.href}>
                              <Link
                                href={child.href}
                                onClick={onClose}
                                className={cn(
                                  "flex items-center px-6 py-3.5 min-h-[44px]",
                                  "text-sm text-white/60 hover:text-white hover:bg-white/5",
                                  "transition-all duration-500 rounded-lg"
                                )}
                              >
                                {t(child.tKey)}
                              </Link>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </>
                  ) : (
                    <Link
                      href={link.href}
                      onClick={onClose}
                      className={cn(
                        "flex items-center px-6 py-4 min-h-[52px]",
                        "text-base font-medium tracking-[0.15em] uppercase",
                        "text-white/90 hover:text-white hover:bg-white/5",
                        "transition-all duration-500 rounded-lg"
                      )}
                    >
                      {t(link.tKey)}
                    </Link>
                  )}
                </li>
              );
            })}
          </ul>
          )}
        </nav>

        {/* Footer */}
        <div
          className={cn(
            "px-6 py-8 border-t border-white/10 transition-all duration-500 ease-out",
            showLinks
              ? "opacity-100 translate-y-0"
              : "opacity-0 translate-y-4"
          )}
          style={{
            transitionDelay: showLinks ? `${NAV_LINKS.length * 60 + 100}ms` : "0ms",
          }}
        >
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="[&>div>button]:text-white/80 [&>div>button:hover]:text-white [&>div>button>span]:inline">
              <LanguageSwitcher />
            </div>
            <div className="[&>div>button]:text-white/80 [&>div>button:hover]:text-white [&>div>button>span]:inline">
              <CurrencySwitcher />
            </div>
          </div>
          <Link
            href={session ? "/account" : "/login"}
            onClick={onClose}
            className={cn(
              "block w-full text-center py-4 min-h-[48px] rounded-lg",
              "border border-white/20 text-sm font-medium tracking-[0.15em] uppercase text-white",
              "hover:bg-white hover:text-brand-dark",
              "transition-all duration-500 ease-out",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold/50"
            )}
          >
            {session ? t("myAccount") : t("signIn")}
          </Link>
          {/* Exit-scope button removed — see Header.tsx for context. */}
        </div>
      </div>
    </>
  );
}
